import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Alert,
  Snackbar
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { Person, Business } from '@mui/icons-material';
import ProfessionalRegisterForm from '../components/ProfessionalRegisterForm';
import OrganizationRegisterForm from '../components/OrganizationRegisterForm';
import AuthSocialButtons from '../components/AuthSocialButtons';
import { clearError, clearRegistrationSuccess } from '../redux/slices/authSlice';
import './Register.css';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`register-tabpanel-${index}`}
      aria-labelledby={`register-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `register-tab-${index}`,
    'aria-controls': `register-tabpanel-${index}`,
  };
}

const Register = () => {
  const [tabValue, setTabValue] = useState(0);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error, registrationSuccess, userType, user } = useSelector(state => state.auth);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    dispatch(clearError());
  };

  useEffect(() => {
    if (registrationSuccess) {
      setShowSuccessSnackbar(true);
      
      // Determine user type and redirect immediately
      // On utilise aussi tabValue comme fallback : si l'utilisateur est sur l'onglet organisation (index 1), c'est une orga
      const isOrganization = userType === 'organization' || user?.role === 'organization' || tabValue === 1;
      const targetPath = isOrganization ? '/dashboard/org' : '/dashboard';
        
      console.log('Inscription réussie. Redirection immédiate vers:', targetPath);
      navigate(targetPath, { replace: true });
      dispatch(clearRegistrationSuccess());
    }
  }, [registrationSuccess, userType, user, navigate, dispatch]);

  useEffect(() => {
    // Clear any existing errors when component mounts
    dispatch(clearError());
  }, [dispatch]);

  const handleCloseSuccessSnackbar = () => {
    setShowSuccessSnackbar(false);
  };

  // Fonction pour personnaliser le message d'erreur
  const getCustomErrorMessage = (error) => {
    if (error === 'Un utilisateur avec cet email ou ce téléphone existe déjà') {
      return 'Cet email ou numéro de téléphone est déjà attribué à un autre utilisateur';
    }
    return error;
  };

  return (
    <>
      <Helmet>
        <title>Inscription - Mission Sport</title>
        <meta 
          name="description" 
          content="Rejoignez Mission Sport en tant que professionnel du sport ou organisation sportive. Créez votre compte et connectez-vous avec la communauté sportive." 
        />
      </Helmet>
      
      <Box className="register-page">
        <Container maxWidth="md" className="register-container">
          <Paper className="register-paper" elevation={3}>
            <Box className="register-header">
              <Typography variant="h3" component="h1" className="register-title">
                Créer un compte
              </Typography>
              <Typography variant="body1" className="register-subtitle">
                Rejoignez la communauté Mission Sport et développez votre réseau professionnel
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" className="register-alert">
                {getCustomErrorMessage(error)}
              </Alert>
            )}

            <Box className="register-tabs-container">
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="Type d'inscription"
                className="register-tabs"
                variant="fullWidth"
              >
                <Tab
                  icon={<Person />}
                  label="Je suis un professionnel"
                  className="register-tab"
                  {...a11yProps(0)}
                />
                <Tab
                  icon={<Business />}
                  label="Je suis une organisation"
                  className="register-tab"
                  {...a11yProps(1)}
                />
              </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
              <ProfessionalRegisterForm loading={loading} />
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <OrganizationRegisterForm loading={loading} />
            </TabPanel>

            <Box className="register-divider">
              <Typography variant="body2" className="divider-text">
                ou
              </Typography>
            </Box>

            <AuthSocialButtons />

            <Box className="register-footer">
              <Typography variant="body2" className="login-link">
                Vous avez déjà un compte ?{' '}
                <Link to="/login" className="link">
                  Se connecter
                </Link>
              </Typography>
            </Box>
          </Paper>
        </Container>

        <Snackbar
          open={showSuccessSnackbar}
          autoHideDuration={6000}
          onClose={handleCloseSuccessSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSuccessSnackbar} severity="success" sx={{ width: '100%' }}>
            🎉 Inscription réussie ! Bienvenue sur Mission Sport
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};

export default Register;